import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";
import { Rocket, FolderPlus, UserPlus, X } from "lucide-react";

// First-run welcome for new Team/Org account owners — nudges them to set up.
export default function OnboardingPanel() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [show, setShow] = useState(false);
  const [acc, setAcc] = useState(null);

  const key = user ? `tm_onboarded_${user.id}` : null;

  useEffect(() => {
    if (!user || user.account_role !== "owner") return;
    if (user.viewing_account_id) return; // platform admin impersonating a tenant — not a first-run
    if (key && localStorage.getItem(key)) return;
    let alive = true;
    (async () => {
      try {
        const [{ data: a }, { data: ws }] = await Promise.all([
          api.get("/account/me"),
          api.get("/workspaces"),
        ]);
        if (!alive) return;
        if ((a.type === "team" || a.type === "organisation") && (ws || []).length === 0) {
          setAcc(a);
          setShow(true);
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [user, key]);

  const dismiss = () => { if (key) localStorage.setItem(key, "1"); setShow(false); };

  if (!show || !acc) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="onboarding-panel">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-slate-900 text-white px-8 py-7 relative">
          <button onClick={dismiss} data-testid="onboarding-dismiss" className="absolute right-4 top-4 text-slate-400 hover:text-white"><X size={18} /></button>
          <Rocket size={28} className="text-cyan-400 mb-3" />
          <h2 className="text-2xl font-bold tracking-tight">Welcome to {acc.name}</h2>
          <p className="text-slate-400 text-sm mt-1.5">Let's get your workspace set up in two quick steps.</p>
        </div>
        <div className="p-6 space-y-3">
          <button data-testid="onboarding-create-ws" onClick={() => { dismiss(); nav("/workspaces"); }}
            className="w-full flex items-center gap-4 rounded-xl border border-slate-200 p-4 text-left hover:border-slate-900 transition-colors">
            <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0"><FolderPlus size={20} /></div>
            <div><div className="font-semibold text-sm text-slate-900">Create your first workspace</div><div className="text-xs text-slate-500 mt-0.5">A home for your team's tasks, notes and documents</div></div>
          </button>
          <button data-testid="onboarding-invite" onClick={() => { dismiss(); nav("/account-settings"); }}
            className="w-full flex items-center gap-4 rounded-xl border border-slate-200 p-4 text-left hover:border-slate-900 transition-colors">
            <div className="h-10 w-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><UserPlus size={20} /></div>
            <div><div className="font-semibold text-sm text-slate-900">Invite your teammates</div><div className="text-xs text-slate-500 mt-0.5">Add people to collaborate in your account</div></div>
          </button>
          <div className="text-center pt-1">
            <button onClick={dismiss} className="text-sm text-slate-500 hover:text-slate-700">I'll do this later</button>
          </div>
        </div>
      </div>
    </div>
  );
}
